import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Progress,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  Divider,
  Select,
} from '@chakra-ui/react';

import { 
  MdPlayArrow, 
  MdPause, 
  MdCheckCircle, 
  MdInfo, 
  MdError,
  MdPending,
  MdArrowBack,
} from 'react-icons/md';

import { useNavigate } from 'react-router-dom';

const API_RSSE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const ITEMS_PER_PAGE_OPTIONS = [20, 50, 100];

const BuscarDadosJurisConsult = () => {
  const [precatorios, setPrecatorios] = useState([]);
  const [precatoriosToProcess, setPrecatoriosToProcess] = useState([]); // Only precatorios without JurisConsult
  const [processing, setProcessing] = useState(false);
  const [paused, setPaused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState([]);
  const toast = useToast();
  const navigate = useNavigate();
  
  // Refs for managing the processing
  const pausedRef = useRef(false);
  const timeoutRef = useRef(null);
  const processingRef = useRef(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const processingBg = useColorModeValue('blue.50', 'blue.900');

  // Status colors
  const statusColors = {
    success: 'green',
    cached: 'blue',
    error: 'red',
    pending: 'gray',
    notFound: 'orange'
  };

  // Status icons
  const statusIcons = {
    success: MdCheckCircle,
    cached: MdInfo,
    error: MdError,
    pending: MdPending,
    notFound: MdInfo
  };

  // Status labels
  const statusLabels = {
    success: 'Dados obtidos',
    cached: 'Já existente',
    error: 'Erro',
    pending: 'Pendente',
    notFound: 'Não encontrado'
  };

  // Fetch selected precatorios on mount
  useEffect(() => {
    fetchSelectedPrecatorios();
  }, []);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const fetchSelectedPrecatorios = async () => {
    try {
      // First, get all selected precatorios
      const res = await fetch(`${API_RSSE_URL}/precatorioRS/selecionados`);
      if (!res.ok) {
        throw new Error('Falha ao buscar precatórios selecionados');
      }
      
      const data = await res.json();
      setPrecatorios(data);
      
      // Check which ones are missing JurisConsult data
      const checkRes = await fetch(`${API_RSSE_URL}/precatorioRS/check-missing-jurisconsult`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          precatorioIds: data.map(p => p._id)
        })
      });
      
      if (!checkRes.ok) {
        throw new Error('Falha ao verificar dados JurisConsult');
      }
      
      const checkData = await checkRes.json();
      
      // Show toast with summary
      toast({
        title: 'Verificação concluída',
        description: `${checkData.withoutJurisConsult} de ${checkData.totalChecked} precatórios precisam buscar dados JurisConsult`,
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      
      // Set only precatorios without JurisConsult data to be processed
      setPrecatoriosToProcess(checkData.precatoriosWithoutData);
      
      // Initialize results array with appropriate status
      const initialResults = data.map(p => {
        const hasJurisConsult = checkData.precatoriosWithData.some(pd => pd._id === p._id);
        return {
          _id: p._id,
          numero_precatorio: p.numero_precatorio,
          valor_atualizado: p.valor_atualizado,
          ente: p.ente,
          status: hasJurisConsult ? 'cached' : 'pending',
          message: hasJurisConsult ? 'Dados já existentes no banco' : 'Aguardando processamento'
        };
      });
      
      setResults(initialResults);
    } catch (err) {
      console.error(err);
      toast({
        title: 'Erro ao carregar precatórios',
        description: err.message || 'Não foi possível carregar a lista de precatórios selecionados',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const processNextPrecatorio = async (indexToProcess = null) => {
    // Use passed index or current index
    const index = indexToProcess !== null ? indexToProcess : currentIndex;
    
    // Check if we should stop
    if (pausedRef.current || index >= precatoriosToProcess.length) {
      setProcessing(false);
      
      if (index >= precatoriosToProcess.length && !pausedRef.current) {
        toast({
          title: 'Processamento concluído',
          description: `Todos os ${precatoriosToProcess.length} precatórios pendentes foram processados.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
      return;
    }

    const precatorio = precatoriosToProcess[index];
    
    try {
      // Update status to processing
      updateResult(index, {
        status: 'processing',
        message: 'Buscando dados...'
      });

      // Make API call
      const response = await fetch(`${API_RSSE_URL}/precatorioRS/jurisconsult/${precatorio._id}`);

      if (response.ok) {
        const data = await response.json();
        
        // Check if precatorio was not found in external API
        if (data.notFound) {
          updateResult(index, {
            status: 'notFound',
            message: data.message || 'Precatório não encontrado na API externa',
            processedAt: new Date().toISOString()
          });
        } else {
          // Check if data was from cache or newly fetched
          const wasCached = data.fromCache === true;
          
          updateResult(index, {
            status: wasCached ? 'cached' : 'success',
            message: wasCached ? 'Dados já existentes no banco' : 'Dados obtidos com sucesso',
            processedAt: new Date().toISOString()
          });
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao buscar dados');
      }
    } catch (error) {
      console.error(`Erro ao processar precatório ${precatorio.numero_precatorio}:`, error);
      
      updateResult(index, {
        status: 'error',
        message: error.message || 'Erro desconhecido',
        processedAt: new Date().toISOString()
      });

      // Auto-pause on error
      pausedRef.current = true;
      setPaused(true);
      setProcessing(false);
      
      toast({
        title: 'Erro no processamento',
        description: `Erro ao processar precatório ${precatorio.numero_precatorio}. Processamento pausado.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      
      return;
    }

    // Move to next precatorio only if not paused
    if (!pausedRef.current) {
      // Update index immediately
      const nextIndex = index + 1;
      setCurrentIndex(nextIndex);
      
      // Small delay before processing next
      timeoutRef.current = setTimeout(() => {
        // Check if we should continue
        if (!pausedRef.current && nextIndex < precatoriosToProcess.length) {
        processNextPrecatorio(nextIndex);
        } else {
        setProcessing(false);
        }
        }, 1000);
    } else {
      setProcessing(false);
    }
  };

  const updateResult = (index, updates) => {
    // Find the actual index in the full results array
    const precatorioId = precatoriosToProcess[index]._id;
    const resultIndex = results.findIndex(r => r._id === precatorioId);
    
    if (resultIndex !== -1) {
      setResults(prev => {
        const newResults = [...prev];
        newResults[resultIndex] = {
          ...newResults[resultIndex],
          ...updates
        };
        return newResults;
      });
    }
  };

  const handleStart = () => {
    setProcessing(true);
    setPaused(false);
    pausedRef.current = false;
    // Don't reset currentIndex - continue from where we are
    processNextPrecatorio();
  };

  const handlePause = () => {
    setPaused(true);
    pausedRef.current = true;
    setProcessing(false);
    
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleResume = () => {
    setPaused(false);
    pausedRef.current = false;
    setProcessing(true);
    processNextPrecatorio(currentIndex);
  };

  // Calculate statistics - only for precatorios without JurisConsult
  const filteredResults = results.filter(r => r.status !== 'cached');
  const stats = {
    total: filteredResults.length,
    success: filteredResults.filter(r => r.status === 'success').length,
    error: filteredResults.filter(r => r.status === 'error').length,
    pending: filteredResults.filter(r => r.status === 'pending').length,
    notFound: filteredResults.filter(r => r.status === 'notFound').length,
    alreadyProcessed: results.filter(r => r.status === 'cached').length
  };

  const progressPercentage = precatoriosToProcess.length > 0 ? (currentIndex / precatoriosToProcess.length) * 100 : 0;
  
  // Pagination calculations - only for precatorios without JurisConsult
  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);
  const paginatedResults = filteredResults.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  // Handle page change
  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Flex 
        direction={{ base: 'column', md: 'row' }} 
        justify="space-between" 
        align={{ base: 'flex-start', md: 'center' }} 
        mb={8}
      >
        <Box mb={{ base: 4, md: 0 }}>
          <Heading 
            fontSize={{ base: '2xl', md: '3xl' }} 
            fontWeight="bold"
            color={headingColor}
            mb={2}
          >
            Buscar Dados JurisConsult
          </Heading>
          <Text color={textColor}>
            Sincronização de dados dos precatórios selecionados
          </Text>
        </Box>
        <Button
          leftIcon={<Icon as={MdArrowBack} />}
          variant="outline"
          size="sm"
          onClick={() => navigate('/precatorios/ba/selecionadas')}
        >
          Voltar
        </Button>
      </Flex>

      {/* Progress Card */}
      <Card
        bg={cardBg}
        borderRadius="lg"
        boxShadow="sm"
        overflow="hidden"
        border="1px"
        borderColor={borderColor}
        mb={6}
      >
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Flex justify="space-between" align="center">
              <Box>
                <Text fontSize="lg" fontWeight="medium">
                  Progresso: {currentIndex} de {precatoriosToProcess.length} precatórios pendentes
                </Text>
                <Text fontSize="sm" color={textColor}>
                  {precatorios.length - precatoriosToProcess.length} precatórios já possuem dados JurisConsult
                </Text>
              </Box>
              <HStack>
                {!processing && currentIndex === 0 && (
                  <Button
                    leftIcon={<Icon as={MdPlayArrow} />}
                    colorScheme="green"
                    onClick={handleStart}
                    isDisabled={precatoriosToProcess.length === 0}
                  >
                    {precatoriosToProcess.length === 0 ? 'Nada para processar' : 'Iniciar'}
                  </Button>
                )}
                {processing && !paused && (
                  <Button
                    leftIcon={<Icon as={MdPause} />}
                    colorScheme="orange"
                    onClick={handlePause}
                  >
                    Pausar
                  </Button>
                )}
                {paused && currentIndex < precatoriosToProcess.length && (
                  <Button
                    leftIcon={<Icon as={MdPlayArrow} />}
                    colorScheme="green"
                    onClick={handleResume}
                  >
                    Continuar
                  </Button>
                )}
              </HStack>
            </Flex>
            
            <Progress 
              value={progressPercentage} 
              size="lg" 
              colorScheme="blue" 
              borderRadius="full"
              hasStripe
              isAnimated={processing}
            />
            
            {paused && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                Processamento pausado. Clique em "Continuar" para retomar.
              </Alert>
            )}
            
            {precatoriosToProcess.length === 0 && !processing && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                Todos os precatórios selecionados já possuem dados JurisConsult.
              </Alert>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Statistics Cards */}
      <SimpleGrid columns={{ base: 1, md: 5 }} spacing={4} mb={6}>
        <Card
          bg={cardBg}
          borderRadius="lg"
          boxShadow="sm"
          overflow="hidden"
          border="1px"
          borderColor={borderColor}
        >
          <CardBody p={4}>
            <Stat>
              <StatLabel color={textColor}>Pendentes</StatLabel>
              <StatNumber>{stats.pending}</StatNumber>
              <StatHelpText>Aguardando</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card
          bg={cardBg}
          borderRadius="lg"
          boxShadow="sm"
          overflow="hidden"
          border="1px"
          borderColor={borderColor}
        >
          <CardBody p={4}>
            <Stat>
              <StatLabel color="green.500">Processados</StatLabel>
              <StatNumber>{stats.success}</StatNumber>
              <StatHelpText>Dados obtidos</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card
          bg={cardBg}
          borderRadius="lg"
          boxShadow="sm"
          overflow="hidden"
          border="1px"
          borderColor={borderColor}
        >
          <CardBody p={4}>
            <Stat>
              <StatLabel color="orange.500">Não Encontrados</StatLabel>
              <StatNumber>{stats.notFound}</StatNumber>
              <StatHelpText>API externa</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card
          bg={cardBg}
          borderRadius="lg"
          boxShadow="sm"
          overflow="hidden"
          border="1px"
          borderColor={borderColor}
        >
          <CardBody p={4}>
            <Stat>
              <StatLabel color="red.500">Erros</StatLabel>
              <StatNumber>{stats.error}</StatNumber>
              <StatHelpText>Falhas</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card
          bg={cardBg}
          borderRadius="lg"
          boxShadow="sm"
          overflow="hidden"
          border="1px"
          borderColor="blue.300"
          borderWidth="2px"
        >
          <CardBody p={4}>
            <Stat>
              <StatLabel color="blue.500">Já Existentes</StatLabel>
              <StatNumber>{stats.alreadyProcessed}</StatNumber>
              <StatHelpText>No banco</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Results Table */}
      <Card
        bg={cardBg}
        borderRadius="lg"
        boxShadow="sm"
        overflow="hidden"
        border="1px"
        borderColor={borderColor}
      >
        <CardHeader pb={2}>
          <Flex justify="space-between" align="center">
            <Heading size="md" color={headingColor}>
              Precatórios Pendentes de Dados JurisConsult
            </Heading>
            <HStack spacing={2}>
              <Select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                size="sm"
                w="auto"
              >
                {ITEMS_PER_PAGE_OPTIONS.map(option => (
                  <option key={option} value={option}>{option} por página</option>
                ))}
              </Select>
            </HStack>
          </Flex>
        </CardHeader>
        
        <CardBody p={0}>
          {filteredResults.length === 0 ? (
            <Box p={8} textAlign="center">
              <Icon as={MdCheckCircle} fontSize="48px" color="green.500" mb={4} />
              <Text fontSize="lg" fontWeight="medium" color={headingColor} mb={2}>
                Todos os precatórios já possuem dados JurisConsult
              </Text>
              <Text color={textColor}>
                Não há precatórios pendentes de processamento.
              </Text>
            </Box>
          ) : (
            <Box overflowX="auto" w="100%">
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th>Status</Th>
                    <Th>Nº Precatório</Th>
                    <Th>Ente</Th>
                    <Th>Valor</Th>
                    <Th>Mensagem</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedResults.map((result, index) => {
                    const actualIndex = (currentPage - 1) * itemsPerPage + index;
                    // Find the index in precatoriosToProcess array
                    const processingIndex = precatoriosToProcess.findIndex(p => p._id === result._id);
                    return (
                      <Tr 
                        key={result._id}
                        _hover={{ bg: hoverBg }}
                        transition="background-color 0.2s"
                        bg={processingIndex === currentIndex && processing ? processingBg : 'transparent'}
                      >
                      <Td>
                        <Badge 
                          colorScheme={statusColors[result.status]} 
                          variant="subtle"
                          display="flex"
                          alignItems="center"
                          gap={1}
                          w="fit-content"
                        >
                          <Icon as={statusIcons[result.status]} />
                          {statusLabels[result.status]}
                        </Badge>
                      </Td>
                      <Td fontWeight="medium">{result.numero_precatorio}</Td>
                      <Td>{result.ente}</Td>
                      <Td>R$ {result.valor_atualizado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</Td>
                      <Td fontSize="sm" color={textColor}>{result.message}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
        
        {filteredResults.length > itemsPerPage && (
          <CardFooter borderTopWidth="1px" borderColor={borderColor} justifyContent="space-between">
            <Text fontSize="sm" color={textColor}>
              Página {currentPage} de {totalPages} ({filteredResults.length} registros pendentes)
            </Text>
            <HStack>
              <Button 
                onClick={() => handlePageChange('prev')} 
                isDisabled={currentPage === 1}
                size="sm"
                variant="outline"
              >
                Anterior
              </Button>
              <Button 
                onClick={() => handlePageChange('next')} 
                isDisabled={currentPage === totalPages}
                size="sm"
                variant="solid"
                colorScheme="blue"
              >
                Próxima
              </Button>
            </HStack>
          </CardFooter>
        )}
      </Card>
    </Box>
  );
};

export default BuscarDadosJurisConsult;